import React, { useEffect, useState, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCog,
  faEdit,
  faPlus,
  faSearch,
  faTrash,
  faUser,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  Col,
  Row,
  Form,
  Button,
  InputGroup,
  Card,
  Table,
  FormLabel,
  Modal,
  Badge,
  Alert,
  Pagination,
} from "@themesberg/react-bootstrap";

import { useNavigate } from "react-router-dom";
import { fetchApi, postApi, getSector } from "../../../redux/api";
import { Routes } from "../../../routes";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { mainTreasuryModules } from "./modules";
import CustomTypeahead from "../../components/CustomTypeahead";

export function getDistinctObjects(array, key1, key2) {
  return array?.filter(
    (obj, index, self) =>
      index ===
      self?.findIndex((o) => o[key1] === obj[key1] && o[key2] === obj[key2])
  );
}

// ─── Pagination Controls ──────────────────────────────────────────────────────
function PaginationControls({ pagination, onPageChange }) {
  if (!pagination || pagination.totalPages <= 1) return null;

  const { page, totalPages, total, limit } = pagination;
  const from = (page - 1) * limit + 1;
  const to   = Math.min(page * limit, total);

  // Build visible page numbers: always show first, last, current ±2, with ellipsis
  const buildPages = () => {
    const pages = [];
    const delta = 2;
    const range = [];

    for (
      let i = Math.max(2, page - delta);
      i <= Math.min(totalPages - 1, page + delta);
      i++
    ) {
      range.push(i);
    }

    if (page - delta > 2) range.unshift("...");
    if (page + delta < totalPages - 1) range.push("...");

    range.unshift(1);
    if (totalPages > 1) range.push(totalPages);

    range.forEach((p) => pages.push(p));
    return pages;
  };

  return (
    <div className="d-flex flex-wrap align-items-center justify-content-between mt-3 gap-2">
      <small className="text-muted">
        Showing <strong>{from}–{to}</strong> of <strong>{total}</strong> users
      </small>

      <Pagination className="mb-0">
        <Pagination.Prev
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </Pagination.Prev>

        {buildPages().map((p, idx) =>
          p === "..." ? (
            <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled />
          ) : (
            <Pagination.Item
              key={p}
              active={p === page}
              onClick={() => p !== page && onPageChange(p)}
            >
              {p}
            </Pagination.Item>
          )
        )}

        <Pagination.Next
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Pagination.Next>
      </Pagination>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
function UsersList() {
  const history     = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);

  // ── Server-side pagination state ──────────────────────────────────────────
  const LIMIT = 50;
  const [users,      setUsers]      = useState([]);
  const [pagination, setPagination] = useState(null);   // metadata from API
  const [currentPage, setCurrentPage] = useState(1);

  // ── Filter state (server-side) ────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMDA, setSelectedMDA] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("active"); // default: active only

  // ── Loading / action state ────────────────────────────────────────────────
  const [loading,    setLoading]    = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Modal / edit state (unchanged from original) ─────────────────────────
  const [showDeleteModal,        setShowDeleteModal]        = useState(false);
  const [showEditModal,          setShowEditModal]          = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm,     setEditForm]     = useState({});
  const [newPassword,  setNewPassword]  = useState("");

  // ── MDA list for the filter dropdown (populated from budget MDA endpoint) ─
  const [budgetSummary, setBudgetSummary] = useState([]);

  // ── Debounce ref ──────────────────────────────────────────────────────────
  const debounceTimer = useRef(null);

  // ── Fetch users from server ───────────────────────────────────────────────
  const fetchUsers = useCallback((page, search, mda, status) => {
    setLoading(true);
    const params = new URLSearchParams({
      page:  page,
      limit: LIMIT,
    });
    if (search) params.set("search", search);
    if (mda)    params.set("mda",    mda);
    // always send status — 'all' means no filter, anything else filters by value
    params.set("status", status || "active");

    fetchApi(`users?${params.toString()}`)
      .then((d) => {
        if (d && d.users) {
          setUsers(d.users);
          setPagination(d.pagination || null);
        } else {
          toast.error("Failed to fetch users");
        }
      })
      .catch(() => toast.error("Failed to fetch users"))
      .finally(() => setLoading(false));
  }, []);

  // Initial load
  useEffect(() => {
    fetchUsers(1, "", "", "active");
  }, [fetchUsers]);

  // Fetch MDA list for the filter dropdown
  useEffect(() => {
    getSector({ type: "mdas" }, "mdas", (d) => setBudgetSummary(d || []));
  }, []);

  // ── Search: debounce 400 ms, then reset to page 1 ────────────────────────
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchUsers(1, value, selectedMDA, selectedStatus);
    }, 400);
  };

  // ── MDA filter: immediate, reset to page 1 ───────────────────────────────
  const handleMDAChange = (e) => {
    const value = e.target.value;
    setSelectedMDA(value);
    setCurrentPage(1);
    fetchUsers(1, searchQuery, value, selectedStatus);
  };

  // ── Status filter: immediate, reset to page 1 ────────────────────────────
  const handleStatusChange = (e) => {
    const value = e.target.value;
    setSelectedStatus(value);
    setCurrentPage(1);
    fetchUsers(1, searchQuery, selectedMDA, value);
  };

  // ── Clear all filters ─────────────────────────────────────────────────────
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedMDA("");
    setSelectedStatus("active");
    setCurrentPage(1);
    fetchUsers(1, "", "", "active");
  };

  // ── Page change ───────────────────────────────────────────────────────────
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchUsers(page, searchQuery, selectedMDA, selectedStatus);
  };

  // ── Edit / Delete / Status / Password handlers (unchanged logic) ──────────
  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      id:              user.id,
      fullname:        user.fullname || user.name || "",
      username:        user.username || "",
      email:           user.email || "",
      role:            user.role || "",
      phone:           user.phone || "",
      status:          user.status || "active",
      accessTo:        user.accessTo ? user.accessTo.split(",").filter(Boolean) : [],
      functionalities: user.functionalities ? user.functionalities.split(",").filter(Boolean) : [],
      mda_code:        user.mda_code || "",
      mda_name:        user.mda_name || "",
    });
    setShowEditModal(true);
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setShowResetPasswordModal(true);
  };

  const confirmResetPassword = () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }
    setSubmitting(true);
    postApi(`admin_reset_password/${selectedUser.id}`, { newPassword })
      .then((r) => {
        if (r.success) {
          toast.success("Password reset successfully");
          setShowResetPasswordModal(false);
          setNewPassword("");
        } else {
          toast.error(r.message || "Failed to reset password");
        }
      })
      .catch(() => toast.error("An error occurred while resetting password"))
      .finally(() => setSubmitting(false));
  };

  const handleStatusToggle = (user) => {
    const newStatus = user.status === "active" ? "suspended" : "active";
    const action    = newStatus === "suspended" ? "suspend" : "activate";
    if (!window.confirm(`Are you sure you want to ${action} ${user.fullname || user.name}?`)) return;
    setSubmitting(true);
    postApi(`update_user_status/${user.id}`, { status: newStatus })
      .then((r) => {
        if (r.success) {
          toast.success(`User ${action}d successfully`);
          fetchUsers(currentPage, searchQuery, selectedMDA, selectedStatus);
        } else {
          toast.error(`Failed to ${action} user`);
        }
      })
      .catch(() => toast.error(`An error occurred while ${action}ing user`))
      .finally(() => setSubmitting(false));
  };

  const confirmDelete = () => {
    setSubmitting(true);
    fetchApi(`users/${selectedUser.id}`, { method: "DELETE" })
      .then((r) => {
        if (r.success) {
          toast.success("User deleted successfully");
          setShowDeleteModal(false);
          // Stay on current page; if page is now empty, go back one
          const newTotal = (pagination?.total || 1) - 1;
          const maxPage  = Math.max(1, Math.ceil(newTotal / LIMIT));
          const page     = Math.min(currentPage, maxPage);
          setCurrentPage(page);
          fetchUsers(page, searchQuery, selectedMDA, selectedStatus);
        } else {
          toast.error("Failed to delete user");
        }
      })
      .catch(() => toast.error("An error occurred while deleting user"))
      .finally(() => setSubmitting(false));
  };

  const handleEditSubmit = () => {
    setSubmitting(true);
    const updateData = {
      id:              editForm.id,
      fullname:        editForm.fullname,
      username:        editForm.username,
      email:           editForm.email,
      role:            editForm.role,
      phone:           editForm.phone,
      accessTo:        editForm.accessTo.join(","),
      functionalities: editForm.functionalities.join(","),
      mda_name:        editForm.mda_name,
      mda_code:        editForm.mda_code,
    };
    postApi(`update_user-api-new/${editForm.id}`, updateData)
      .then((r) => {
        if (r.success) {
          toast.success("User updated successfully");
          setShowEditModal(false);
          fetchUsers(currentPage, searchQuery, selectedMDA, selectedStatus);
        } else {
          toast.error(r.message || "Failed to update user");
        }
      })
      .catch(() => toast.error("An error occurred while updating user"))
      .finally(() => setSubmitting(false));
  };

  const handleEditChange = ({ target: { name, value } }) =>
    setEditForm((p) => ({ ...p, [name]: value }));

  const toggleAccess = (access) => {
    const title = typeof access === "string" ? access : access.title;
    setEditForm((prev) => {
      const newAccessTo = prev.accessTo.includes(title)
        ? prev.accessTo.filter((a) => a !== title)
        : [...prev.accessTo, title];
      let newFunctionalities = prev.functionalities;
      if (!newAccessTo.includes(title) && access.subMenu) {
        const subMenuTitles = access.subMenu.map((s) => s.title);
        newFunctionalities = prev.functionalities.filter((f) => !subMenuTitles.includes(f));
      }
      return { ...prev, accessTo: newAccessTo, functionalities: newFunctionalities };
    });
  };

  const toggleFunctionality = (functionality) => {
    const title = typeof functionality === "string" ? functionality : functionality.title;
    setEditForm((prev) => ({
      ...prev,
      functionalities: prev.functionalities.includes(title)
        ? prev.functionalities.filter((f) => f !== title)
        : [...prev.functionalities, title],
    }));
  };

  const renderSubMenu = (subMenu) =>
    subMenu.map((subItem, i) => (
      <div key={i} style={{ marginLeft: 20 }}>
        <Form.Check
          type="checkbox"
          label={subItem.title}
          checked={editForm.functionalities?.includes(subItem.title)}
          onChange={() => toggleFunctionality(subItem)}
        />
      </div>
    ));

  const getStatusBadge = (status) => {
    const variant =
      status === "active"   ? "success" :
      status === "inactive" ? "danger"  : "warning";
    return <Badge bg={variant}>{status || "active"}</Badge>;
  };

  // Row serial number offset based on current page
  const rowOffset = ((currentPage - 1) * LIMIT);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Page header */}
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>User Management</h4>
          <p className="mb-0">Manage system users and their permissions</p>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <Button
            className="me-2"
            onClick={() => history(`${Routes.userAdmin.editUser}`)}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Create New User
          </Button>
        </div>
      </div>

      {/* Filters row */}
      <Row className="mb-3">
        <Col md={4}>
          <FormLabel>Search Users</FormLabel>
          <InputGroup>
            <Form.Control
              placeholder="Search by name, username, email, or role"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <Button variant="outline-secondary" tabIndex={-1}>
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </InputGroup>
        </Col>

        <Col md={3}>
          <FormLabel>Filter by MDA</FormLabel>
          <Form.Select value={selectedMDA} onChange={handleMDAChange}>
            <option value="">All MDAs</option>
            {getDistinctObjects(
              budgetSummary?.map((i) => ({ label: i.description })) || [],
              "label",
              "label"
            ).map((item, idx) => (
              <option key={idx} value={item.label}>
                {item.label}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={2}>
          <FormLabel>Filter by Status</FormLabel>
          <Form.Select value={selectedStatus} onChange={handleStatusChange}>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="inactive">Inactive</option>
            <option value="disabled">Disabled</option>
            <option value="all">All Statuses</option>
          </Form.Select>
        </Col>

        <Col md={3} className="d-flex align-items-end justify-content-between">
          <div className="text-muted small">
            {pagination ? (
              <>
                <strong>{pagination.total}</strong> user{pagination.total !== 1 ? "s" : ""} found
                {selectedMDA && <span> · MDA: {selectedMDA}</span>}
                {searchQuery && <span> · Search: "{searchQuery}"</span>}
              </>
            ) : (
              "Loading…"
            )}
          </div>
          {(searchQuery || selectedMDA) && (
            <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </Col>
      </Row>

      {/* Table card */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          ) : users.length === 0 ? (
            <Alert variant="info">
              {searchQuery || selectedMDA
                ? "No users found matching your filters."
                : "No users found."}
            </Alert>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped hover>
                  <thead className="table-dark">
                    <tr>
                      <th>S/N</th>
                      <th>Name</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>MDA</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={user.id || index}>
                        <td>{rowOffset + index + 1}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faUser} className="me-2 text-muted" />
                            <strong>{user.fullname || user.name}</strong>
                          </div>
                        </td>
                        <td>{user.username}</td>
                        <td>{user.email}</td>
                        <td>
                          <Badge bg="primary">{user.role}</Badge>
                        </td>
                        <td>{user.mda_name || "N/A"}</td>
                        <td>{getStatusBadge(user.status)}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleEdit(user)}
                              title="Edit user"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </Button>
                            <Button
                              size="sm"
                              variant={user.status === "active" ? "outline-warning" : "outline-success"}
                              onClick={() => handleStatusToggle(user)}
                              title={user.status === "active" ? "Suspend user" : "Activate user"}
                            >
                              <FontAwesomeIcon icon={user.status === "active" ? faTimes : faCheck} />
                            </Button>
                            {currentUser?.role?.toLowerCase() === "admin" && (
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => handleResetPassword(user)}
                                title="Reset password"
                              >
                                <FontAwesomeIcon icon={faCog} />
                              </Button>
                            )}
                            {currentUser?.role?.toLowerCase() === "admin" &&
                              user.id !== currentUser.id && (
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDelete(user)}
                                  title="Delete user"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </Button>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination controls */}
              <PaginationControls
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {/* ── Edit User Modal ───────────────────────────────────────────────── */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Edit User: {selectedUser?.fullname || selectedUser?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="fullname"
                    value={editForm.fullname}
                    onChange={handleEditChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={editForm.username}
                    onChange={handleEditChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={editForm.email}
                    onChange={handleEditChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={editForm.phone}
                    onChange={handleEditChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    name="role"
                    value={editForm.role}
                    onChange={handleEditChange}
                  >
                    <option value="">Select Role</option>
                    <option value="Admin">Admin</option>
                    <option value="MDA ADMIN">MDA Admin</option>
                    <option value="USER">User</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <CustomTypeahead
                    labelKey="mda_name"
                    label="Select MDA"
                    options={getDistinctObjects(
                      budgetSummary?.map((i) => ({
                        mda_name: i.description,
                        mda_code: i.head,
                      })) || [],
                      "mda_name",
                      "mda_code"
                    )}
                    selected={
                      editForm.mda_name
                        ? [{ mda_name: editForm.mda_name, mda_code: editForm.mda_code }]
                        : []
                    }
                    onChange={(s) => {
                      if (s.length) {
                        setEditForm((p) => ({
                          ...p,
                          mda_code: s[0].mda_code,
                          mda_name: s[0].mda_name,
                        }));
                      } else {
                        setEditForm((p) => ({ ...p, mda_code: "", mda_name: "" }));
                      }
                    }}
                    placeholder="Search and select MDA..."
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={editForm.status}
                    onChange={handleEditChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <h6 className="mb-3">Permissions and Access Level</h6>
            <Alert variant="info" className="mb-3">
              <small>Select the modules and functionalities this user should have access to.</small>
            </Alert>
            <Row>
              {mainTreasuryModules.map((item, index) => (
                <Col md="6" key={index} className="mb-3">
                  <Card className="h-100">
                    <Card.Body className="p-3">
                      <Form.Check
                        type="checkbox"
                        label={<strong>{item.title}</strong>}
                        checked={editForm.accessTo?.includes(item.title)}
                        onChange={() => toggleAccess(item)}
                        className="mb-2"
                      />
                      {editForm.accessTo?.includes(item.title) && item.subMenu && (
                        <div className="ms-3">{renderSubMenu(item.subMenu)}</div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
              <Col md="6" className="mb-3">
                <Card className="h-100">
                  <Card.Body className="p-3">
                    <Form.Check
                      type="checkbox"
                      label={<strong>User Admin</strong>}
                      checked={editForm.accessTo?.includes("User Admin")}
                      onChange={() => toggleAccess("User Admin")}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditSubmit} disabled={submitting}>
            {submitting ? (
              <><span className="spinner-border spinner-border-sm me-2" />Updating…</>
            ) : (
              "Update User"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Reset Password Modal ──────────────────────────────────────────── */}
      <Modal
        show={showResetPasswordModal}
        onHide={() => setShowResetPasswordModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Reset Password: {selectedUser?.fullname || selectedUser?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (minimum 6 characters)"
                minLength={6}
              />
              <Form.Text className="text-muted">
                Password must be at least 6 characters long.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowResetPasswordModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirmResetPassword}
            disabled={submitting || !newPassword || newPassword.length < 6}
          >
            {submitting ? (
              <><span className="spinner-border spinner-border-sm me-2" />Resetting…</>
            ) : (
              "Reset Password"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete Confirmation Modal ─────────────────────────────────────── */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Are you sure you want to delete user{" "}
            <strong>{selectedUser?.fullname || selectedUser?.name}</strong>?
            This action cannot be undone.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={submitting}>
            {submitting ? (
              <><span className="spinner-border spinner-border-sm me-2" />Deleting…</>
            ) : (
              "Delete User"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default UsersList;
